import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BookOpenText,
  BrainCircuit,
  CalendarDays,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Download,
  Eye,
  FileText,
  Filter,
  Image,
  Loader2,
  LockKeyhole,
  MessageSquare,
  Paperclip,
  Pencil,
  Plus,
  Search,
  ShieldAlert,
  Trash2,
  Upload,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import GuideUtilisationDrawer from './GuideUtilisationDrawer';
import { Toast } from './ui/Toast';
import { useSearchParams } from 'react-router-dom';
import { useFeatures } from '../contexts/FeaturesContext';
import { usePermissions } from '../hooks/usePermissions';
import AvisEnseignantsOnglet from './avisEnseignants/AvisEnseignantsOnglet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import useCompteurAvisNouveaux from './avisEnseignants/useCompteurAvisNouveaux';
import useVieScolaire, {
  GRAVITE_OPTIONS,
  STATUT_OPTIONS,
  TYPE_OPTIONS,
} from '../hooks/useVieScolaire';

// Gabarits KLASYS (DESIGN-SYSTEM.md) : champs h-9 rounded-lg, cartes rounded-lg,
// boutons de pied de même taille.
const CHAMP = 'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15 disabled:bg-gray-50 disabled:text-slate-400';
const ZONE_TEXTE = 'w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
const CARTE = 'rounded-lg border border-gray-200 bg-white p-4';
const BOUTON_PRINCIPAL = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md bg-[#246990] px-4 text-sm font-medium text-white transition hover:bg-[#1f5a7a] disabled:cursor-not-allowed disabled:opacity-50';
const BOUTON_SECONDAIRE = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';
const BOUTON_PETIT = 'inline-flex h-8 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';
const LIBELLE = 'mb-1 block text-sm font-medium text-slate-700';

const TYPE_LABELS = Object.fromEntries(TYPE_OPTIONS.map((item) => [item.value, item.label]));
const GRAVITE_LABELS = Object.fromEntries(GRAVITE_OPTIONS.map((item) => [item.value, item.label]));
const STATUT_LABELS = Object.fromEntries(STATUT_OPTIONS.map((item) => [item.value, item.label]));

const TYPE_STYLES = {
  absence: 'bg-sky-50 text-sky-700 border-sky-200',
  retard: 'bg-amber-50 text-amber-700 border-amber-200',
  incident: 'bg-orange-50 text-orange-700 border-orange-200',
  sanction: 'bg-rose-50 text-rose-700 border-rose-200',
  degradation: 'bg-red-50 text-red-700 border-red-200',
  convocation: 'bg-violet-50 text-violet-700 border-violet-200',
  entretien: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  observation_positive: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  note_interne: 'bg-slate-50 text-slate-700 border-slate-200',
  autre: 'bg-gray-50 text-gray-700 border-gray-200',
};

const GRAVITE_STYLES = {
  info: 'bg-slate-100 text-slate-700',
  leger: 'bg-lime-100 text-lime-700',
  moyen: 'bg-amber-100 text-amber-700',
  grave: 'bg-orange-100 text-orange-700',
  critique: 'bg-red-100 text-red-700',
};

const GRAVITE_COLORS = {
  info: '#64748b',
  leger: '#65a30d',
  moyen: '#d97706',
  grave: '#ea580c',
  critique: '#dc2626',
};

const STATUT_STYLES = {
  nouveau: 'bg-blue-100 text-blue-700',
  en_cours: 'bg-amber-100 text-amber-700',
  traite: 'bg-emerald-100 text-emerald-700',
  clos: 'bg-slate-100 text-slate-700',
  archive: 'bg-gray-100 text-gray-600',
};

const HISTORY_LABELS = {
  creation: 'Création',
  modification: 'Modification',
  commentaire: 'Information ajoutée',
  ajout_piece: 'Pièce jointe',
  cloture: 'Clôture',
  suppression: 'Suppression',
};

const JOURNAL_PAGE_SIZE = 5;

const getInitials = (eleve) => {
  const prenom = eleve?.prenom?.[0] || '';
  const nom = eleve?.nom?.[0] || '';
  return `${prenom}${nom}`.toUpperCase() || 'EL';
};

const toDatetimeLocal = (date = new Date()) => {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const isAttachmentRequired = (type) => type === 'degradation';

const createDefaultForm = (eleveId = '') => ({
  eleve_id: eleveId,
  type: 'incident',
  titre: '',
  description: '',
  date_evenement: toDatetimeLocal(),
  lieu: '',
  gravite: 'moyen',
  statut: 'nouveau',
  action_prise: '',
  suivi_requis: false,
  date_suivi: '',
});

const Badge = ({ children, className = '' }) => (
  <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${className}`}>
    {children}
  </span>
);

const StatCard = ({ icon: Icon, label, value, color = 'from-[#246990] to-[#3084b5]', helper }) => (
  <div className="overflow-hidden rounded-lg border-0 shadow-md">
    <div className={`bg-gradient-to-r ${color} p-4 text-white`}>
      <div className="mb-2 flex items-center gap-1 text-sm font-medium">
        <Icon size={16} className="text-white" />
        <span className="text-xs leading-tight">{label}</span>
      </div>
      <p className="hidden text-xs text-white/80 md:block">{helper || 'Stats actuelles'}</p>
      <div className="mt-2 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold">{Number(value || 0)}</span>
      </div>
    </div>
  </div>
);

const FlowStep = ({ icon: Icon, title, text, color }) => (
  <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-start gap-3">
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${color}14`, color }}
      >
        <Icon size={19} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="mt-1 text-xs leading-5 text-slate-500">{text}</p>
      </div>
    </div>
  </div>
);

const AppSelect = ({ value, onChange, placeholder, options, className = '', searchable = false }) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const currentValue = value === undefined || value === null ? '' : String(value);

  const normalizedOptions = useMemo(
    () => options.map((option) => ({
      ...option,
      value: option.value === undefined || option.value === null ? '' : String(option.value),
    })),
    [options]
  );

  const selectedOption = normalizedOptions.find((option) => option.value === currentValue);
  const filteredOptions = searchable
    ? normalizedOptions.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : normalizedOptions;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setIsOpen(false);
        }}
        className="flex h-9 w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 text-left text-sm outline-none transition hover:border-gray-300 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={`truncate ${selectedOption ? 'text-slate-800' : 'text-slate-400'}`}>
          {selectedOption?.label || placeholder}
        </span>
        <svg className="ml-2 h-4 w-4 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md">
          {searchable && (
            <div className="border-b border-gray-100 p-2">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="h-9 w-full rounded-lg border border-gray-200 pl-9 pr-3 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                  placeholder="Rechercher..."
                  autoFocus
                />
              </div>
            </div>
          )}

          <div className="max-h-64 overflow-y-auto" role="listbox">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-center text-sm text-gray-500">Aucun résultat</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value || 'empty'}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-[#eef7fb] ${option.value === currentValue ? 'bg-[#eef7fb] font-medium text-[#246990]' : 'text-gray-800'
                    }`}
                  role="option"
                  aria-selected={option.value === currentValue}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ title, text }) => (
  <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
    <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
      <FileText size={20} />
    </div>
    <h3 className="mt-3 text-sm font-semibold text-slate-900">{title}</h3>
    <p className="mt-1 text-sm text-slate-500">{text}</p>
  </div>
);

const EleveAvatar = ({ eleve }) => (
  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-xs font-semibold text-slate-600">
    {eleve?.photo_url ? (
      <img src={eleve.photo_url} alt="" className="h-9 w-9 rounded-lg object-cover" />
    ) : (
      getInitials(eleve)
    )}
  </div>
);

const VieScolairePage = () => {
  // L'ecran annoncait « Lié à l'outil décisionnel » et une etape « Synthese
  // decisionnelle » a des ecoles qui n'ont pas cet outil. Promettre une suite
  // qui n'existe pas est pire que ne rien promettre : on cherche l'ecran.
  // La chaine garde son sens sans elles — constat, suivi, decision.
  const { estActive } = useFeatures();
  const outilDecisionnelOuvert = estActive('module.evaluations.outil_decisionnel');

  // Onglet « Avis des enseignants » (espace enseignant, lot D) : les avis que
  // les enseignants donnent sur leurs élèves. Il n'existe que si l'école a
  // ouvert l'espace enseignant ET que l'employé peut lire ces avis — même
  // composition licence × clé fine que « Messages des familles » dans le menu.
  // Sans lui, la page reste exactement ce qu'elle était, sans barre d'onglets.
  // Le serveur refuse de toute façon (requireFeature, permission) : ce test
  // évite un onglet qui ne mènerait qu'à un refus.
  const { detailedPermissions = [] } = usePermissions();
  const avisOuverts = estActive('module.portail_enseignant') && detailedPermissions.some(
    (permission) => (permission.permission_key || permission.key) === 'ELEVES.AVIS_ENSEIGNANTS.VIEW'
  );
  // L'onglet vit dans l'adresse (`?onglet=avis`) : le suivi des enseignants y
  // envoie directement, et un rechargement de page ne ramène pas ailleurs.
  const [parametres, setParametres] = useSearchParams();
  const onglet = avisOuverts && parametres.get('onglet') === 'avis' ? 'avis' : 'evenements';
  const choisirOnglet = (cle) => {
    const suivants = new URLSearchParams(parametres);
    if (cle === 'avis') suivants.set('onglet', 'avis');
    else suivants.delete('onglet');
    setParametres(suivants, { replace: true });
  };
  const [avisNouveaux, majAvisNouveaux] = useCompteurAvisNouveaux(avisOuverts);
  const surCompteursAvis = useCallback((compteurs) => majAvisNouveaux(compteurs?.nouveau), [majAvisNouveaux]);

  const {
    dashboard,
    evenements,
    eleves,
    classes,
    pagination,
    loadingDashboard,
    loadingEvenements,
    loadingEleves,
    error,
    loadDashboard,
    loadEvenements,
    loadEleves,
    getEvenement,
    createEvenement,
    updateEvenement,
    deleteEvenement,
    addNoteEvenement,
    closeEvenement,
    addPiecesJointes,
    fetchPieceJointeBlob,
    downloadPieceJointe,
  } = useVieScolaire();

  const [filters, setFilters] = useState({
    search: '',
    classe_id: '',
    type: '',
    gravite: '',
    statut: '',
  });
  const [selectedEleveId, setSelectedEleveId] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(createDefaultForm());
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editingEventId, setEditingEventId] = useState(null);
  const [formEleveSearch, setFormEleveSearch] = useState('');
  const [noteText, setNoteText] = useState('');
  const [closureText, setClosureText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [toastInfo, setToastInfo] = useState(null);
  const [journalPage, setJournalPage] = useState(1);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const videoRef = useRef(null);

  const selectedEleve = useMemo(
    () => eleves.find((eleve) => String(eleve.id) === String(selectedEleveId)),
    [eleves, selectedEleveId]
  );

  const selectedFormEleve = useMemo(
    () => eleves.find((eleve) => String(eleve.id) === String(form.eleve_id)),
    [eleves, form.eleve_id]
  );

  const stats = dashboard?.stats || {};
  const attachmentRequired = isAttachmentRequired(form.type);
  const canSubmit = form.eleve_id && form.description && form.date_evenement && (editingEventId || !attachmentRequired || files.length > 0);
  const showElevesList = filters.search.trim().length >= 2 || filters.classe_id || selectedEleveId;
  const isDetailClosed = detail?.statut === 'clos';

  const classeOptions = useMemo(
    () => [
      { value: '', label: 'Toutes les classes' },
      ...classes.map((classe) => ({ value: String(classe.id), label: classe.nom })),
    ],
    [classes]
  );
  const typeFilterOptions = useMemo(() => [{ value: '', label: 'Tous' }, ...TYPE_OPTIONS], []);
  const statutFilterOptions = useMemo(() => [{ value: '', label: 'Tous' }, ...STATUT_OPTIONS], []);
  const graviteFilterOptions = useMemo(() => [{ value: '', label: 'Toutes' }, ...GRAVITE_OPTIONS], []);

  const journalItems = detail?.historique || [];
  const journalTotalPages = Math.max(1, Math.ceil(journalItems.length / JOURNAL_PAGE_SIZE));
  const journalStart = (journalPage - 1) * JOURNAL_PAGE_SIZE;
  const paginatedJournal = journalItems.slice(journalStart, journalStart + JOURNAL_PAGE_SIZE);

  const showToast = (message, type = 'success') => {
    setToastInfo({ message, type });
  };

  const refreshCurrentView = useCallback(() => {
    const eventFilters = {
      ...filters,
      eleve_id: selectedEleveId,
      page: 1,
      limit: pagination.limit || 30,
    };

    loadEvenements(eventFilters);
    loadEleves({ search: filters.search, classe_id: filters.classe_id });
    loadDashboard();
  }, [filters, loadDashboard, loadEleves, loadEvenements, pagination.limit, selectedEleveId]);

  const stopCamera = useCallback(() => {
    cameraStream?.getTracks?.().forEach((track) => track.stop());
    setCameraStream(null);
    setCameraOpen(false);
    setCameraError('');
  }, [cameraStream]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      loadEvenements({
        ...filters,
        eleve_id: selectedEleveId,
        page: 1,
        limit: pagination.limit || 30,
      });

      if (filters.search.trim().length >= 2 || filters.classe_id) {
        loadEleves({ search: filters.search, classe_id: filters.classe_id });
      }
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [filters, loadEleves, loadEvenements, pagination.limit, selectedEleveId]);

  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  useEffect(() => {
    if (cameraOpen && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch(() => { });
    }
  }, [cameraOpen, cameraStream]);

  useEffect(() => {
    return () => {
      cameraStream?.getTracks?.().forEach((track) => track.stop());
    };
  }, [cameraStream]);

  useEffect(() => {
    if (!isModalOpen || formEleveSearch.trim().length < 2) return;

    const timeout = window.setTimeout(() => {
      loadEleves({ search: formEleveSearch, classe_id: filters.classe_id });
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [filters.classe_id, formEleveSearch, isModalOpen, loadEleves]);

  useEffect(() => {
    setJournalPage(1);
  }, [detail?.id]);

  useEffect(() => {
    if (journalPage > journalTotalPages) {
      setJournalPage(journalTotalPages);
    }
  }, [journalPage, journalTotalPages]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({ search: '', classe_id: '', type: '', gravite: '', statut: '' });
    setSelectedEleveId('');
  };

  const openCreateModal = (eleveId = selectedEleveId) => {
    setForm(createDefaultForm(eleveId || ''));
    setFiles([]);
    setEditingEventId(null);
    setFormEleveSearch('');
    setSubmitError('');
    setIsModalOpen(true);
  };

  const openEditModal = (evenement) => {
    if (evenement.statut === 'clos') {
      showToast('Cet événement est clôturé et ne peut plus être modifié', 'error');
      return;
    }

    setDetail(null);
    setEditingEventId(evenement.id);
    setForm({
      eleve_id: evenement.eleve_id || '',
      type: evenement.type || 'incident',
      titre: evenement.titre || '',
      description: evenement.description || '',
      date_evenement: evenement.date_evenement ? toDatetimeLocal(new Date(evenement.date_evenement)) : toDatetimeLocal(),
      lieu: evenement.lieu || '',
      gravite: evenement.gravite || 'moyen',
      statut: evenement.statut || 'nouveau',
      action_prise: evenement.action_prise || '',
      suivi_requis: Boolean(evenement.suivi_requis),
      date_suivi: evenement.date_suivi ? String(evenement.date_suivi).slice(0, 10) : '',
    });
    setFormEleveSearch(`${evenement.eleve?.prenom || ''} ${evenement.eleve?.nom || ''}`.trim());
    setFiles([]);
    setSubmitError('');
    // Le détail se ferme dans ce rendu : ouvrir l'édition dans le même rendu
    // laisserait la page inerte (deux fenêtres Radix fermée et ouverte ensemble).
    setTimeout(() => setIsModalOpen(true), 200);
  };

  const closeCreateModal = () => {
    setIsModalOpen(false);
    setFiles([]);
    setEditingEventId(null);
    setFormEleveSearch('');
    setSubmitError('');
    stopCamera();
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleSuiviRequis = () => {
    setForm((prev) => {
      const nextValue = !prev.suivi_requis;
      return {
        ...prev,
        suivi_requis: nextValue,
        date_suivi: nextValue ? prev.date_suivi : '',
      };
    });
  };

  const handleFileSelection = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) return;

    setFiles((prev) => [...prev, ...selectedFiles].slice(0, 10));
    event.target.value = '';
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const openCamera = async () => {
    setCameraError('');

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("La caméra n'est pas disponible dans ce navigateur.");
      setCameraOpen(true);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      setCameraStream(stream);
      setCameraOpen(true);
    } catch (err) {
      setCameraError("Impossible d'ouvrir la caméra. Vérifiez les autorisations du navigateur.");
      setCameraOpen(true);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;

    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo-vie-scolaire-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setFiles((prev) => [...prev, file].slice(0, 10));
      stopCamera();
      showToast('Photo ajoutée aux pièces jointes', 'success');
    }, 'image/jpeg', 0.9);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setSubmitError('');

    const result = editingEventId
      ? await updateEvenement(editingEventId, {
        ...form,
        commentaire_historique: 'Modification depuis la page Vie scolaire',
      })
      : await createEvenement(form, files);

    if (!result.success) {
      setSubmitError(result.error || "Erreur lors de l'enregistrement");
      showToast(result.error || "Erreur lors de l'enregistrement", 'error');
      setSubmitting(false);
      return;
    }

    if (editingEventId && files.length > 0) {
      const piecesResult = await addPiecesJointes(editingEventId, files);
      if (piecesResult.success) {
        result.data = piecesResult.data;
      } else {
        showToast(piecesResult.error || "L'événement est modifié, mais les pièces n'ont pas été ajoutées", 'error');
      }
    }

    setSubmitting(false);
    closeCreateModal();
    if (detail?.id === result.data?.id) {
      setDetail(result.data);
    }
    showToast(editingEventId ? 'Événement modifié avec succès' : 'Événement enregistré avec succès', 'success');
    refreshCurrentView();
  };

  const handleOpenDetail = async (id) => {
    setLoadingDetail(true);
    setDetail(null);

    try {
      const data = await getEvenement(id);
      setDetail(data);
      setNoteText('');
      setClosureText('');
    } catch (err) {
      showToast(err.message, 'error');
      setSubmitError(err.message);
    } finally {
      setLoadingDetail(false);
    }
  };

  const requestDelete = (evenement) => {
    setConfirmDialog({
      title: 'Supprimer cet événement ?',
      description: "Cette action masque l'événement du suivi. Pour terminer un suivi, utilisez plutôt la clôture.",
      confirmText: 'Supprimer',
      type: 'warning',
      onConfirm: async () => {
        const result = await deleteEvenement(evenement.id);
        if (!result.success) {
          showToast(result.error || 'Erreur lors de la suppression', 'error');
          return false;
        }

        setDetail(null);
        refreshCurrentView();
        showToast('Événement supprimé avec succès', 'success');
        return true;
      },
    });
  };

  const requestCloseEvent = () => {
    if (!detail?.id || !closureText.trim()) {
      showToast('La solution trouvee est requise pour cloturer', 'error');
      return;
    }

    setConfirmDialog({
      title: 'Cloturer ce suivi ?',
      description: 'Une fois clôturé, cet événement ne pourra plus être modifié.',
      confirmText: 'Cloturer',
      type: 'success',
      onConfirm: async () => {
        const result = await closeEvenement(detail.id, closureText.trim());
        if (!result.success) {
          showToast(result.error || 'Erreur lors de la clôture', 'error');
          return false;
        }

        setDetail(result.data);
        setClosureText('');
        refreshCurrentView();
        showToast('Suivi clôturé avec succès', 'success');
        return true;
      },
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog?.onConfirm) return;

    setConfirmLoading(true);
    const shouldClose = await confirmDialog.onConfirm();
    setConfirmLoading(false);

    if (shouldClose !== false) {
      setConfirmDialog(null);
    }
  };

  const handleAddNote = async () => {
    if (!detail?.id || !noteText.trim()) return;
    if (isDetailClosed) {
      showToast('Cet événement est clôturé et ne peut plus recevoir de modification', 'error');
      return;
    }

    setActionLoading(true);
    const result = await addNoteEvenement(detail.id, noteText.trim());
    setActionLoading(false);

    if (result.success) {
      setDetail(result.data);
      setNoteText('');
      refreshCurrentView();
      showToast('Information ajoutée avec succès', 'success');
    } else {
      showToast(result.error || "Erreur lors de l'ajout", 'error');
      setSubmitError(result.error);
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const handlePreviewPiece = async (piece) => {
    try {
      const { blob, filename, contentType } = await fetchPieceJointeBlob(piece.id, 'view');
      const url = URL.createObjectURL(blob);
      closePreview();
      setPreview({
        url,
        filename: filename || piece.nom_fichier_original,
        contentType: contentType || piece.mime_type,
      });
    } catch (err) {
      showToast(err.message, 'error');
      setSubmitError(err.message);
    }
  };

  const handleDownloadPiece = async (piece) => {
    try {
      await downloadPieceJointe(piece.id, piece.nom_fichier_original);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const renderAttachmentPreview = () => {
    if (!preview) return null;

    if (preview.contentType?.startsWith('image/')) {
      return (
        <img
          src={preview.url}
          alt={preview.filename}
          className="max-h-[70vh] w-full rounded-lg object-contain"
        />
      );
    }

    if (preview.contentType?.includes('pdf')) {
      return (
        <iframe
          title={preview.filename}
          src={preview.url}
          className="h-[70vh] w-full rounded-lg border border-slate-200"
        />
      );
    }

    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center">
        <FileText className="mx-auto text-slate-500" size={34} />
        <p className="mt-3 text-sm font-medium text-slate-800">{preview.filename}</p>
      </div>
    );
  };

  const selectEleveForForm = (eleve) => {
    setForm((prev) => ({ ...prev, eleve_id: eleve.id }));
    setFormEleveSearch(`${eleve.prenom} ${eleve.nom}`);
  };

  return (
    <div className="min-h-screen bg-[#f6f8fb] p-4 text-slate-900 sm:p-6">
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold leading-tight text-[#246990]">Vie scolaire</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setIsGuideOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-[#246990]/20 bg-[#246990]/10 px-3 py-2 text-sm font-semibold text-[#246990] transition hover:bg-[#246990]/15"
          >
            <BookOpenText size={16} />
            Guide d'utilisation
          </button>

          <button
            type="button"
            onClick={() => openCreateModal()}
            className="inline-flex items-center gap-2 rounded-lg bg-[#246990] px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1f5b7d]"
          >
            <Plus size={16} />
            Nouvel événement
          </button>
        </div>
      </div>

      {/* Les onglets du projet : primitives Tabs, pastilles pleines sur fond
          #246990 a 10 %, en grille pleine largeur (PARAMETRAGE-MODULES.md
          section 2 ; motif a deux onglets copie de ClasseMatieres.jsx). Cette
          page etait la seule a les construire a la main, en onglets soulignes.

          L'onglet reste dans l'adresse (`?onglet=avis`) : `Tabs` est pilote en
          `value` / `onValueChange`, l'etat n'est pas repris en interne. La
          navigation au clavier vient de Radix, et le panneau inactif est
          demonte comme avant.

          Sans l'espace enseignant, il n'y a pas de barre d'onglets : la page
          reste exactement ce qu'elle etait. */}
      <Tabs value={onglet} onValueChange={choisirOnglet} className="w-full">
        {avisOuverts && (
          <TabsList className="grid grid-cols-2 mb-6 bg-[#246990]/10 w-full">
            <TabsTrigger
              value="evenements"
              className="flex items-center justify-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white min-w-0"
            >
              <CalendarDays size={16} className="shrink-0" />
              <span className="truncate">Événements</span>
            </TabsTrigger>

            <TabsTrigger
              value="avis"
              className="group flex items-center justify-center gap-2 data-[state=active]:bg-[#319190] data-[state=active]:text-white min-w-0"
            >
              <MessageSquare size={16} className="shrink-0" />
              <span className="truncate">Avis des enseignants</span>
              {Number(avisNouveaux) > 0 && (
                <span className="inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-lg bg-[#319190] px-1.5 text-[11px] font-semibold text-white group-data-[state=active]:bg-white/25">
                  <span className="sr-only">, nouveaux : </span>
                  {avisNouveaux}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="avis" className="mt-0 focus-visible:outline-none">
          <AvisEnseignantsOnglet classes={classes} onCompteurs={surCompteursAvis} />
        </TabsContent>

        <TabsContent value="evenements" className="mt-0 focus-visible:outline-none">
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {submitError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {submitError}
        </div>
      )}

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <StatCard icon={Activity} label="Événements 30j" value={stats.total_30j} color="from-[#246990] to-[#3084b5]" helper="Activité récente" />
        <StatCard icon={Clock3} label="Ouverts" value={stats.ouverts} color="from-[#c66d59] to-[#ae7467]" helper="À traiter" />
        <StatCard icon={ShieldAlert} label="Graves" value={stats.graves} color="from-[#8B5CF6] to-[#A855F7]" helper="Priorité haute" />
        <StatCard icon={Users} label="Absences" value={stats.absences} color="from-[#319190] to-[#63b5b4]" helper="Signalements" />
        <StatCard icon={CheckCircle2} label="Suivis" value={stats.suivis_a_faire} color="from-gray-800 to-gray-700" helper="Suivi requis" />
      </div>

      <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              {outilDecisionnelOuvert ? 'Chaîne de suivi décisionnel' : 'Chaîne de suivi'}
            </h2>
            <p className="text-xs text-slate-500">Chaque signal terrain peut devenir une aide concrète pour la direction.</p>
          </div>
          {outilDecisionnelOuvert && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#246990]/10 px-3 py-1 text-xs font-semibold text-[#246990]">
              <BrainCircuit size={13} />
              Lié à l'outil décisionnel
            </span>
          )}
        </div>
        <div className={`grid gap-3 lg:items-stretch ${outilDecisionnelOuvert
          ? 'lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr]'
          : 'lg:grid-cols-[1fr_auto_1fr_auto_1fr]'}`}>
          <FlowStep icon={Camera} title="Constat terrain" text="Observation, absence, incident, photo ou justificatif." color="#246990" />
          <div className="hidden items-center justify-center text-slate-300 lg:flex">
            <ArrowRight size={22} />
          </div>
          <FlowStep icon={MessageSquare} title="Suivi Vie scolaire" text="Commentaires, historique, suivi requis et clôture." color="#319190" />
          <div className="hidden items-center justify-center text-slate-300 lg:flex">
            <ArrowRight size={22} />
          </div>
          {outilDecisionnelOuvert && (
            <>
              <FlowStep icon={BrainCircuit} title="Synthèse décisionnelle" text="Les signaux graves ou suivis requis enrichissent l'analyse." color="#7c3aed" />
              <div className="hidden items-center justify-center text-slate-300 lg:flex">
                <ArrowRight size={22} />
              </div>
            </>
          )}
          <FlowStep icon={ClipboardCheck} title="Décision direction" text="Décision ciblée, plan d'accompagnement ou contact parent." color="#c66d59" />
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[360px_1fr]">
        <aside className="space-y-4 xl:sticky xl:top-4 xl:self-start">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#246990]/10 text-[#246990]">
                  <Filter size={16} />
                </span>
                Filtres
              </div>
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs font-medium text-[#246990] hover:text-[#1d5878]"
              >
                Réinitialiser
              </button>
            </div>

            <div className="space-y-3 rounded-lg bg-slate-50/70 p-3">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Recherche élève ou événement</span>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
                  <input
                    type="search"
                    value={filters.search}
                    onChange={(event) => updateFilter('search', event.target.value)}
                    className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                    placeholder="Minimum 2 lettres"
                  />
                </div>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Classe</span>
                <AppSelect
                  value={filters.classe_id}
                  onChange={(value) => updateFilter('classe_id', value)}
                  placeholder="Toutes les classes"
                  options={classeOptions}
                  searchable
                />
              </label>

              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-600">Type</span>
                  <AppSelect
                    value={filters.type}
                    onChange={(value) => updateFilter('type', value)}
                    placeholder="Tous"
                    options={typeFilterOptions}
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-600">Statut</span>
                  <AppSelect
                    value={filters.statut}
                    onChange={(value) => updateFilter('statut', value)}
                    placeholder="Tous"
                    options={statutFilterOptions}
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Gravité</span>
                <AppSelect
                  value={filters.gravite}
                  onChange={(value) => updateFilter('gravite', value)}
                  placeholder="Toutes"
                  options={graviteFilterOptions}
                />
              </label>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#319190]/10 text-[#319190]">
                  <UserRound size={16} />
                </span>
                Recherche élève
              </div>
              {loadingEleves && <Loader2 className="animate-spin text-slate-400" size={16} />}
            </div>

            <div className="max-h-[360px] overflow-y-auto p-2">
              {selectedEleveId && (
                <button
                  type="button"
                  onClick={() => setSelectedEleveId('')}
                  className="mb-2 w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-100"
                >
                  Voir tous les événements
                </button>
              )}

              {!showElevesList && (
                <p className="rounded-lg bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                  Recherchez un élève ou filtrez par classe.
                </p>
              )}

              {showElevesList && eleves.map((eleve) => (
                <button
                  type="button"
                  key={eleve.id}
                  onClick={() => setSelectedEleveId(String(eleve.id))}
                  className={`mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition ${String(selectedEleveId) === String(eleve.id) ? 'bg-[#eef7fb] text-[#246990]' : 'hover:bg-slate-100'
                    }`}
                >
                  <EleveAvatar eleve={eleve} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {eleve.prenom} {eleve.nom}
                    </p>
                    <p className="truncate text-xs text-slate-500">{eleve.classe_nom || 'Classe non définie'}</p>
                  </div>
                  {(Number(eleve.total_ouverts) > 0 || Number(eleve.total_30j) > 0) && (
                    <span className="rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">
                      {Number(eleve.total_ouverts || eleve.total_30j)}
                    </span>
                  )}
                </button>
              ))}

              {showElevesList && !loadingEleves && eleves.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-slate-500">Aucun élève trouvé</p>
              )}
            </div>
          </div>
        </aside>

        <main className="space-y-5">
          <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-600">
                    <AlertTriangle size={16} />
                  </span>
                  Élèves à suivre
                </div>
                {loadingDashboard && <Loader2 className="animate-spin text-slate-400" size={16} />}
              </div>

              <div className="space-y-2">
                {(dashboard.watchList || []).slice(0, 5).map((eleve) => (
                  <button
                    type="button"
                    key={eleve.id}
                    onClick={() => setSelectedEleveId(String(eleve.id))}
                    className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left transition hover:border-red-100 hover:bg-red-50/40"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        {eleve.prenom} {eleve.nom}
                      </p>
                      <p className="text-xs text-slate-500">{eleve.classe_nom}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-900">{Number(eleve.suivis_ouverts || eleve.total_30j || 0)}</p>
                      <p className="text-xs text-slate-500">a traiter</p>
                    </div>
                  </button>
                ))}

                {!loadingDashboard && (!dashboard.watchList || dashboard.watchList.length === 0) && (
                  <p className="rounded-lg bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                    Aucun suivi prioritaire
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#246990]/10 text-[#246990]">
                  <Activity size={16} />
                </span>
                Répartition
              </div>
              <div className="space-y-2">
                {(dashboard.byType || []).slice(0, 6).map((item) => (
                  <div key={item.type} className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate text-slate-600">{TYPE_LABELS[item.type] || item.type}</span>
                    <span className="font-semibold text-slate-900">{Number(item.total || 0)}</span>
                  </div>
                ))}
                {!loadingDashboard && (!dashboard.byType || dashboard.byType.length === 0) && (
                  <p className="rounded-lg bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                    Aucune donnée récente
                  </p>
                )}
              </div>
            </div>
          </div>

          <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">
                  Historique {selectedEleve ? `- ${selectedEleve.prenom} ${selectedEleve.nom}` : ''}
                </h2>
                <p className="text-sm text-slate-500">{Number(pagination.total || 0)} événement(s) trouvé(s)</p>
              </div>
              {selectedEleve && (
                <button
                  type="button"
                  onClick={() => openCreateModal(selectedEleve.id)}
                  className="inline-flex items-center gap-2 rounded-lg border border-[#246990] bg-white px-3 py-2 text-sm font-medium text-[#246990] transition hover:bg-[#eef7fb]"
                >
                  <Plus size={16} />
                  Ajouter pour cet élève
                </button>
              )}
            </div>

            {loadingEvenements ? (
              <div className="flex h-44 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                <Loader2 className="animate-spin text-[#246990]" size={28} />
              </div>
            ) : evenements.length === 0 ? (
              <EmptyState title="Aucun événement" text="Les prochains suivis apparaîtront ici." />
            ) : (
              <div className="space-y-3">
                {evenements.map((evenement) => {
                  const accentColor = GRAVITE_COLORS[evenement.gravite] || GRAVITE_COLORS.info;
                  return (
                  <article
                    key={evenement.id}
                    className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: accentColor }} />
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="mb-3 flex flex-wrap items-center gap-2">
                          <Badge className={`border ${TYPE_STYLES[evenement.type] || TYPE_STYLES.autre}`}>
                            {TYPE_LABELS[evenement.type] || evenement.type}
                          </Badge>
                          <Badge className={GRAVITE_STYLES[evenement.gravite] || GRAVITE_STYLES.moyen}>
                            {GRAVITE_LABELS[evenement.gravite] || evenement.gravite}
                          </Badge>
                          <Badge className={STATUT_STYLES[evenement.statut] || STATUT_STYLES.nouveau}>
                            {STATUT_LABELS[evenement.statut] || evenement.statut}
                          </Badge>
                          {evenement.suivi_requis && evenement.statut !== 'clos' && (
                            <Badge className="bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                              Suivi requis
                            </Badge>
                          )}
                          {Number(evenement.pieces_count) > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500">
                              <Paperclip size={13} />
                              {evenement.pieces_count}
                            </span>
                          )}
                        </div>

                        <div className="flex min-w-0 items-start gap-3">
                          <EleveAvatar eleve={evenement.eleve} />
                          <div className="min-w-0 flex-1">
                            <h3 className="truncate text-base font-semibold text-slate-900">
                              {evenement.titre || evenement.description}
                            </h3>
                            <p className="mt-0.5 truncate text-xs font-medium text-slate-500">
                              {evenement.eleve?.prenom} {evenement.eleve?.nom}
                              {evenement.eleve?.classe_nom ? ` - ${evenement.eleve.classe_nom}` : ''}
                            </p>
                          </div>
                        </div>
                        <p className="mt-1 line-clamp-2 text-sm text-slate-600">{evenement.description}</p>

                        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                          <span>{formatDateTime(evenement.date_evenement)}</span>
                          {evenement.createur_nom && <span>Créé par {evenement.createur_nom}</span>}
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center gap-2 lg:opacity-80 lg:transition lg:group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => handleOpenDetail(evenement.id)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-100"
                          title="Voir"
                        >
                          <Eye size={16} />
                        </button>
                        {evenement.statut !== 'clos' && (
                          <button
                            type="button"
                            onClick={() => openEditModal(evenement)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-100"
                            title="Modifier"
                          >
                            <Pencil size={16} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => requestDelete(evenement)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 transition hover:bg-red-50"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </article>
                  );
                })}
              </div>
            )}
          </section>
        </main>
      </div>
        </TabsContent>
      </Tabs>

      {/* ================================================================== */}
      {/* Nouvel événement / modifier l'événement                            */}
      {/* ================================================================== */}
      <Dialog open={isModalOpen} onOpenChange={(ouvert) => { if (!ouvert) closeCreateModal(); }}>
        <DialogContent
          className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
          // Un clic à côté ou Échap ne doit pas perdre la saisie
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
                {editingEventId ? <Pencil size={18} /> : <Plus size={18} />}
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  {editingEventId ? "Modifier l'événement" : 'Nouvel événement'}
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500">Vie scolaire</DialogDescription>
              </div>
            </DialogHeader>

            <div className="grid flex-1 gap-4 overflow-y-auto bg-gray-50 px-5 py-4 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="space-y-4">
                {submitError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {submitError}
                  </div>
                )}

                <div className={CARTE}>
                  <div className="mb-4 flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-[#246990]" />
                    <h3 className="text-sm font-semibold text-slate-900">Contexte de l'événement</h3>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="block">
                      <span className={LIBELLE}>Élève</span>
                      <div className="relative">
                        <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
                        <input
                          type="search"
                          value={formEleveSearch}
                          onChange={(event) => {
                            setFormEleveSearch(event.target.value);
                            if (form.eleve_id) handleFormChange('eleve_id', '');
                          }}
                          className={`${CHAMP} pl-9`}
                          placeholder="Rechercher un élève"
                          disabled={Boolean(editingEventId)}
                        />
                      </div>

                      {selectedFormEleve && (
                        <div className="mt-2 flex items-center gap-2 rounded-lg border border-[#246990]/20 bg-[#eef7fb] px-3 py-2 text-sm text-[#246990]">
                          <EleveAvatar eleve={selectedFormEleve} />
                          <div>
                            <p className="font-medium">{selectedFormEleve.prenom} {selectedFormEleve.nom}</p>
                            <p className="text-xs">{selectedFormEleve.classe_nom}</p>
                          </div>
                        </div>
                      )}

                      {!editingEventId && formEleveSearch.trim().length >= 2 && !form.eleve_id && (
                        <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                          {eleves.map((eleve) => (
                            <button
                              type="button"
                              key={eleve.id}
                              onClick={() => selectEleveForForm(eleve)}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#eef7fb]"
                            >
                              <EleveAvatar eleve={eleve} />
                              <div>
                                <p className="font-medium text-slate-800">{eleve.prenom} {eleve.nom}</p>
                                <p className="text-xs text-slate-500">{eleve.classe_nom}</p>
                              </div>
                            </button>
                          ))}
                          {!loadingEleves && eleves.length === 0 && (
                            <p className="px-3 py-4 text-center text-sm text-slate-500">Aucun élève trouvé</p>
                          )}
                        </div>
                      )}
                    </label>

                    <label className="block">
                      <span className={LIBELLE}>Date et heure</span>
                      <input
                        type="datetime-local"
                        value={form.date_evenement}
                        onChange={(event) => handleFormChange('date_evenement', event.target.value)}
                        className={CHAMP}
                        required
                      />
                    </label>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-3">
                    <label className="block">
                      <span className={LIBELLE}>Type</span>
                      <AppSelect
                        value={form.type}
                        onChange={(value) => handleFormChange('type', value)}
                        placeholder="Type"
                        options={TYPE_OPTIONS}
                      />
                    </label>

                    <label className="block">
                      <span className={LIBELLE}>Gravité</span>
                      <AppSelect
                        value={form.gravite}
                        onChange={(value) => handleFormChange('gravite', value)}
                        placeholder="Gravité"
                        options={GRAVITE_OPTIONS}
                      />
                    </label>

                    <label className="block">
                      <span className={LIBELLE}>Statut</span>
                      <AppSelect
                        value={form.statut}
                        onChange={(value) => handleFormChange('statut', value)}
                        placeholder="Statut"
                        options={STATUT_OPTIONS.filter((option) => option.value !== 'clos')}
                      />
                    </label>
                  </div>
                </div>

                <div className={CARTE}>
                  <div className="mb-4 flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-[#319190]" />
                    <h3 className="text-sm font-semibold text-slate-900">Détails et mesures prises</h3>
                  </div>

                  <label className="block">
                    <span className={LIBELLE}>Titre</span>
                    <input
                      type="text"
                      value={form.titre}
                      onChange={(event) => handleFormChange('titre', event.target.value)}
                      className={CHAMP}
                      placeholder="Ex : dégradation d'une table en salle 4"
                    />
                  </label>

                  <label className="mt-4 block">
                    <span className={LIBELLE}>Description</span>
                    <textarea
                      value={form.description}
                      onChange={(event) => handleFormChange('description', event.target.value)}
                      rows={5}
                      className={ZONE_TEXTE}
                      required
                    />
                  </label>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <label className="block">
                      <span className={LIBELLE}>Lieu</span>
                      <input
                        type="text"
                        value={form.lieu}
                        onChange={(event) => handleFormChange('lieu', event.target.value)}
                        className={CHAMP}
                        placeholder="Cour, salle, couloir"
                      />
                    </label>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <button
                        type="button"
                        onClick={toggleSuiviRequis}
                        className="flex w-full items-center justify-between gap-3 text-left"
                        aria-pressed={form.suivi_requis}
                      >
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-slate-700">Suivi requis</span>
                          <span className="block text-xs text-slate-500">Programmer un point de suivi</span>
                        </span>
                        <span
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${form.suivi_requis ? 'bg-[#319190]' : 'bg-slate-300'
                            }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${form.suivi_requis ? 'translate-x-6' : 'translate-x-1'
                              }`}
                          />
                        </span>
                      </button>

                      <div className="mt-3">
                        <span className={LIBELLE}>Date de suivi</span>
                        <input
                          type="date"
                          value={form.date_suivi}
                          onChange={(event) => handleFormChange('date_suivi', event.target.value)}
                          disabled={!form.suivi_requis}
                          className={CHAMP}
                        />
                      </div>
                    </div>
                  </div>

                  <label className="mt-4 block">
                    <span className={LIBELLE}>Action prise</span>
                    <textarea
                      value={form.action_prise}
                      onChange={(event) => handleFormChange('action_prise', event.target.value)}
                      rows={3}
                      className={ZONE_TEXTE}
                    />
                  </label>
                </div>
              </div>

              <aside className="space-y-4">
                <div className={CARTE}>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-800">Pièces jointes</p>
                    {attachmentRequired && !editingEventId && <Badge className="bg-red-100 text-red-700">Requis</Badge>}
                  </div>

                  <div className="grid gap-2">
                    <button
                      type="button"
                      onClick={openCamera}
                      className="flex h-11 items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 text-sm font-medium text-slate-700 transition hover:border-[#246990] hover:bg-[#eef7fb]"
                    >
                      <Camera size={16} />
                      Ouvrir la caméra
                    </button>

                    <label className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:border-[#246990] hover:bg-[#eef7fb]">
                      <Upload size={16} />
                      Ajouter un fichier
                      <input
                        type="file"
                        accept="image/*,.pdf,.doc,.docx"
                        multiple
                        className="hidden"
                        onChange={handleFileSelection}
                      />
                    </label>
                  </div>

                  {files.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {files.map((file, index) => (
                        <div key={`${file.name}-${index}`} className="flex items-center gap-2 rounded-md bg-gray-50 px-2 py-2">
                          {file.type.startsWith('image/') ? <Image size={15} /> : <FileText size={15} />}
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-medium text-slate-700">{file.name}</p>
                            <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="rounded-md p-1 text-slate-400 transition hover:bg-white hover:text-red-600"
                            aria-label="Retirer le fichier"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </aside>
            </div>

            <DialogFooter className="gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0">
              <button type="button" onClick={closeCreateModal} className={BOUTON_SECONDAIRE}>
                Annuler
              </button>
              <button type="submit" disabled={!canSubmit || submitting} className={BOUTON_PRINCIPAL}>
                {submitting ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                {editingEventId ? 'Enregistrer les modifications' : 'Enregistrer'}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* Caméra                                                             */}
      {/* ================================================================== */}
      <Dialog open={cameraOpen} onOpenChange={(ouvert) => { if (!ouvert) stopCamera(); }}>
        <DialogContent className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <Camera size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">Caméra</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">Photographier une pièce jointe</DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {cameraError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {cameraError}
              </div>
            ) : (
              <video
                // La vidéo n'existe qu'une fois la fenêtre montée : le flux est
                // attaché à ce moment-là, et non par un effet qui passerait avant.
                ref={(element) => {
                  videoRef.current = element;
                  if (element && cameraStream && element.srcObject !== cameraStream) {
                    element.srcObject = cameraStream;
                    element.play().catch(() => { });
                  }
                }}
                autoPlay
                playsInline
                muted
                className="max-h-[65vh] w-full rounded-lg bg-slate-950 object-contain"
              />
            )}
          </div>

          <DialogFooter className="gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0">
            <button type="button" onClick={stopCamera} className={BOUTON_SECONDAIRE}>
              {cameraError ? 'Fermer' : 'Annuler'}
            </button>
            {!cameraError && (
              <button type="button" onClick={capturePhoto} className={BOUTON_PRINCIPAL}>
                <Camera size={16} />
                Capturer
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* Détail d'un événement                                              */}
      {/* ================================================================== */}
      <Dialog open={Boolean(detail || loadingDetail)} onOpenChange={(ouvert) => { if (!ouvert) setDetail(null); }}>
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <FileText size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="truncate text-lg font-semibold text-gray-900">Détail de l'événement</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">Vie scolaire</DialogDescription>
            </div>
          </DialogHeader>

          {loadingDetail || !detail ? (
            <div className="flex h-72 items-center justify-center bg-gray-50">
              <Loader2 className="animate-spin text-[#246990]" size={30} />
            </div>
          ) : (
            <div className="grid flex-1 gap-4 overflow-y-auto bg-gray-50 px-5 py-4 lg:grid-cols-[minmax(0,1fr)_330px]">
              <main className="space-y-4">
                <section className={CARTE}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={`border ${TYPE_STYLES[detail.type] || TYPE_STYLES.autre}`}>
                        {TYPE_LABELS[detail.type] || detail.type}
                      </Badge>
                      <Badge className={GRAVITE_STYLES[detail.gravite] || GRAVITE_STYLES.moyen}>
                        {GRAVITE_LABELS[detail.gravite] || detail.gravite}
                      </Badge>
                      <Badge className={STATUT_STYLES[detail.statut] || STATUT_STYLES.nouveau}>
                        {STATUT_LABELS[detail.statut] || detail.statut}
                      </Badge>
                      {isDetailClosed && (
                        <Badge className="bg-slate-100 text-slate-700">
                          Verrouillé
                        </Badge>
                      )}
                    </div>

                    {!isDetailClosed && (
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => openEditModal(detail)} className={BOUTON_PETIT}>
                          <Pencil size={14} />
                          Modifier
                        </button>
                        <button
                          type="button"
                          onClick={() => document.getElementById('closure-box')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                          className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-emerald-300 bg-white px-3 text-xs font-medium text-emerald-700 transition hover:bg-emerald-50"
                        >
                          <LockKeyhole size={14} />
                          Clôturer
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    <h3 className="text-lg font-semibold text-slate-950">{detail.titre || TYPE_LABELS[detail.type]}</h3>
                    <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-700">{detail.description}</p>
                  </div>
                </section>

                <section className={CARTE}>
                  <div className="mb-4 flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-[#246990]" />
                    <h4 className="text-sm font-semibold text-slate-900">Informations générales</h4>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-md bg-gray-50 p-3">
                      <p className="text-xs font-medium uppercase text-slate-500">Élève</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        {detail.eleve?.prenom} {detail.eleve?.nom}
                      </p>
                      <p className="text-xs text-slate-500">{detail.eleve?.classe_nom}</p>
                    </div>
                    <div className="rounded-md bg-gray-50 p-3">
                      <p className="text-xs font-medium uppercase text-slate-500">Date</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{formatDateTime(detail.date_evenement)}</p>
                    </div>
                    <div className="rounded-md bg-gray-50 p-3">
                      <p className="text-xs font-medium uppercase text-slate-500">Créé par</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{detail.createur_nom || '-'}</p>
                      <p className="text-xs text-slate-500">{formatDateTime(detail.created_at)}</p>
                    </div>
                    <div className="rounded-md bg-gray-50 p-3">
                      <p className="text-xs font-medium uppercase text-slate-500">Dernière modification</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{detail.modificateur_nom || '-'}</p>
                      <p className="text-xs text-slate-500">{formatDateTime(detail.updated_at)}</p>
                    </div>
                    <div className="rounded-md bg-gray-50 p-3">
                      <p className="text-xs font-medium uppercase text-slate-500">Lieu</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{detail.lieu || '-'}</p>
                    </div>
                    <div className="rounded-md bg-gray-50 p-3">
                      <p className="text-xs font-medium uppercase text-slate-500">Suivi</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        {detail.suivi_requis ? formatDate(detail.date_suivi) : 'Non requis'}
                      </p>
                    </div>
                  </div>
                </section>

                {detail.action_prise && (
                  <section className={CARTE}>
                    <div className="mb-3 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-[#319190]" />
                      <h4 className="text-sm font-semibold text-slate-900">Action prise</h4>
                    </div>
                    <p className="whitespace-pre-line rounded-md bg-gray-50 p-3 text-sm leading-6 text-slate-700">
                      {detail.action_prise}
                    </p>
                  </section>
                )}

                {detail.solution_cloture && (
                  <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                    <div className="mb-3 flex items-center gap-2 text-emerald-900">
                      <CheckCircle2 size={16} />
                      <h4 className="text-sm font-semibold">Solution de clôture</h4>
                    </div>
                    <p className="whitespace-pre-line text-sm leading-6 text-emerald-800">{detail.solution_cloture}</p>
                    <p className="mt-3 text-xs font-medium text-emerald-700">
                      Clôturé par {detail.clotureur_nom || '-'} le {formatDateTime(detail.cloture_at)}
                    </p>
                  </section>
                )}

                <section className={CARTE}>
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <MessageSquare size={16} />
                    Ajouter une information
                  </div>
                  {isDetailClosed ? (
                    <p className="rounded-md bg-gray-50 px-3 py-4 text-sm text-slate-500">
                      Ce suivi est clôturé. Les informations sont verrouillées.
                    </p>
                  ) : (
                    <>
                      <textarea
                        value={noteText}
                        onChange={(event) => setNoteText(event.target.value)}
                        rows={4}
                        className={ZONE_TEXTE}
                      />
                      <div className="mt-3 flex justify-end">
                        <button
                          type="button"
                          onClick={handleAddNote}
                          disabled={!noteText.trim() || actionLoading}
                          className={BOUTON_PRINCIPAL}
                        >
                          {actionLoading ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}
                          Ajouter
                        </button>
                      </div>
                    </>
                  )}
                </section>
              </main>

              <aside className="space-y-4">
                <section id="closure-box" className="rounded-lg border border-emerald-200 bg-white p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-emerald-900">
                    <LockKeyhole size={16} />
                    Clôture du suivi
                  </div>
                  {isDetailClosed ? (
                    <p className="rounded-md bg-emerald-50 px-3 py-4 text-sm text-emerald-700">
                      Ce suivi est déjà clôturé.
                    </p>
                  ) : (
                    <>
                      <textarea
                        value={closureText}
                        onChange={(event) => setClosureText(event.target.value)}
                        rows={4}
                        className={`${ZONE_TEXTE} focus:border-emerald-600 focus:ring-emerald-600/15`}
                        placeholder="Solution trouvée, mesure appliquée, accord parent, décision direction..."
                      />
                      <button
                        type="button"
                        onClick={requestCloseEvent}
                        disabled={!closureText.trim() || actionLoading}
                        className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <CheckCircle2 size={15} />
                        Clôturer
                      </button>
                    </>
                  )}
                </section>

                <section className={CARTE}>
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <Paperclip size={16} />
                      Pièces jointes
                    </div>
                    <Badge className="bg-slate-100 text-slate-600">
                      {detail.pieces_jointes?.length || 0}
                    </Badge>
                  </div>

                  {detail.pieces_jointes?.length ? (
                    <div className="space-y-2">
                      {detail.pieces_jointes.map((piece) => (
                        <div key={piece.id} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                          <div className="flex items-center gap-3">
                            {piece.mime_type?.startsWith('image/') ? (
                              <Image className="text-slate-500" size={18} />
                            ) : (
                              <FileText className="text-slate-500" size={18} />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-slate-800">{piece.nom_fichier_original}</p>
                              <p className="text-xs text-slate-500">{formatFileSize(piece.taille_fichier)}</p>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            <button type="button" onClick={() => handlePreviewPiece(piece)} className={BOUTON_PETIT}>
                              <Eye size={14} />
                              Voir
                            </button>
                            <button type="button" onClick={() => handleDownloadPiece(piece)} className={BOUTON_PETIT}>
                              <Download size={14} />
                              Télécharger
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="rounded-md bg-gray-50 px-3 py-4 text-sm text-slate-500">Aucune pièce jointe</p>
                  )}
                </section>

                <section className={CARTE}>
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <Clock3 size={16} />
                      Journal
                    </div>
                    {journalItems.length > JOURNAL_PAGE_SIZE && (
                      <p className="text-xs text-slate-500">
                        {journalPage}/{journalTotalPages}
                      </p>
                    )}
                  </div>

                  {journalItems.length ? (
                    <>
                      <div className="space-y-2">
                        {paginatedJournal.map((item) => (
                          <div key={item.id} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="text-sm font-medium text-slate-800">{HISTORY_LABELS[item.action] || item.action}</p>
                              <p className="text-xs text-slate-500">{formatDateTime(item.created_at)}</p>
                            </div>
                            {item.commentaire && (
                              <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{item.commentaire}</p>
                            )}
                            <p className="mt-1 text-xs text-slate-500">Par {item.auteur_nom || '-'}</p>
                          </div>
                        ))}
                      </div>

                      {journalItems.length > JOURNAL_PAGE_SIZE && (
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setJournalPage((page) => Math.max(1, page - 1))}
                            disabled={journalPage <= 1}
                            className={BOUTON_PETIT}
                          >
                            Précédent
                          </button>
                          <button
                            type="button"
                            onClick={() => setJournalPage((page) => Math.min(journalTotalPages, page + 1))}
                            disabled={journalPage >= journalTotalPages}
                            className={BOUTON_PETIT}
                          >
                            Suivant
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="rounded-md bg-gray-50 px-3 py-4 text-sm text-slate-500">Aucun historique</p>
                  )}
                </section>
              </aside>
            </div>
          )}

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <button type="button" onClick={() => setDetail(null)} className={BOUTON_PRINCIPAL}>
              Fermer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* Aperçu d'une pièce jointe                                          */}
      {/* ================================================================== */}
      <Dialog open={Boolean(preview)} onOpenChange={(ouvert) => { if (!ouvert) closePreview(); }}>
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <Paperclip size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="truncate text-lg font-semibold text-gray-900">{preview?.filename || 'Pièce jointe'}</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">Aperçu</DialogDescription>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {preview && renderAttachmentPreview()}
          </div>
          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <button type="button" onClick={closePreview} className={BOUTON_PRINCIPAL}>
              Fermer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {confirmDialog && (
        <ConfirmDialog
          isOpen={Boolean(confirmDialog)}
          onClose={() => setConfirmDialog(null)}
          onConfirm={handleConfirmAction}
          title={confirmDialog.title}
          description={confirmDialog.description}
          confirmText={confirmDialog.confirmText}
          cancelText="Annuler"
          type={confirmDialog.type || 'warning'}
          isLoading={confirmLoading}
        />
      )}

      {toastInfo && (
        <Toast
          message={toastInfo.message}
          type={toastInfo.type}
          onClose={() => setToastInfo(null)}
        />
      )}

      <GuideUtilisationDrawer
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
        module="vieScolaire"
      />
    </div>
  );
};

export default VieScolairePage;
